export const LEGACY_PER_JOB_PLANS = ["plan_simple_ats_per_job", "plan_simple_ats_per_job_tiered"];

export const LEGACY_UNLIMITED_JOBS_PLANS = [
  ...LEGACY_PER_JOB_PLANS,
  "plan_ats_tier_apollo",
  "plan_simple_ats_paid",
];

export const LEGACY_UNLIMITED_USERS_PLANS = [
  "plan_ats_tier_starter",
  "plan_ats_tier_growth",
  "plan_ats_tier_scale",
];

export const LEGACY_FREE_WITH_ONE_JOB_PLANS = ["plan_ats_tier_free", "plan_ats_tier_free_v2"];

export const ALL_LEGACY_PLANS = [
  ...LEGACY_UNLIMITED_JOBS_PLANS,
  ...LEGACY_UNLIMITED_USERS_PLANS,
  ...LEGACY_FREE_WITH_ONE_JOB_PLANS,
];

export const CURRENT_PLAN_NAMES: { [key: string]: string } = {
  plan_no_plan: "Legacy Free",
  plan_simple_ats_free: "Legacy Free",
  plan_ats_tier_free_v2: "Free",
  plan_ats_tier_free: "Legacy Free",
  plan_ats_tier_starter: "Legacy Starter",
  plan_ats_tier_growth: "Legacy Growth",
  plan_ats_tier_scale: "Legacy Scale",
  plan_ats_tier_starter_v2: "Starter",
  plan_ats_tier_growth_v2: "Growth",
  plan_ats_tier_scale_v2: "Scale",
  plan_ats_tier_enterprise: "Enterprise",
};

export const getPricingDisplayName = (planKey: string): string => {
  if (LEGACY_PER_JOB_PLANS.includes(planKey)) {
    return "legacy per job plan";
  }
  if (LEGACY_UNLIMITED_JOBS_PLANS.includes(planKey)) {
    return "legacy unlimited jobs plan";
  }
  return CURRENT_PLAN_NAMES[planKey] || "Unknown";
};

export const getModalDisplayName = (planKey: string): string => {
  return CURRENT_PLAN_NAMES[planKey] || "Unknown";
};

export type LegacyPlanType = "perJob" | "unlimited" | "free" | "other";

export const legacyPlanType = (planKey: string): LegacyPlanType => {
  if (LEGACY_PER_JOB_PLANS.includes(planKey)) return "perJob";
  if (LEGACY_UNLIMITED_JOBS_PLANS.includes(planKey)) return "unlimited";
  if (LEGACY_FREE_WITH_ONE_JOB_PLANS.includes(planKey)) return "free";
  return "other";
};

export const isLegacyPlan = (planKey: string): boolean => {
  return ALL_LEGACY_PLANS.includes(planKey);
};

export const isLegacyUnlimitedJobsPlan = (planKey: string): boolean => {
  return LEGACY_UNLIMITED_JOBS_PLANS.includes(planKey);
};

// Credit allocations served from the backend via window.AI_CREDIT_ALLOCATIONS
// (set in account_application.html.erb from Variables::AI_CREDIT_ALLOCATIONS).
// Fallback to hardcoded defaults if the window variable is not set.
const AI_CREDIT_ALLOCATIONS_FALLBACK: { [lookupKey: string]: number } = {
  plato_ai_credit_top_up_small: 250,
  plato_ai_credit_top_up_medium: 500,
  plato_ai_credit_top_up_large: 1000,
  plato_ai_credit_subscription_small: 500,
  plato_ai_credit_subscription_medium: 1000,
  plato_ai_credit_subscription_large: 2000,
  ai_credit_pack_top_up_small: 250,
  ai_credit_pack_top_up_large: 1000,
  ai_credit_pack_subscription_small_monthly: 500,
  ai_credit_pack_subscription_large_monthly: 2000,
};

const parseAllocations = (): { [lookupKey: string]: number } => {
  const raw = (window as any).AI_CREDIT_ALLOCATIONS;
  if (raw) {
    try { return JSON.parse(raw); } catch (e) { return AI_CREDIT_ALLOCATIONS_FALLBACK; }
  }
  return AI_CREDIT_ALLOCATIONS_FALLBACK;
};

export const AI_CREDIT_PACK_CREDITS_BY_LOOKUP_KEY: { [lookupKey: string]: number } = parseAllocations();

// Monthly plan credit allocations served from the backend via window.AI_MONTHLY_CREDIT_ALLOCATIONS
// (set in the layouts from Variables::AI_MONTHLY_CREDIT_*). Keyed by plan card key.
export const aiMonthlyCreditsByPlanKey: { [planKey: string]: number } = JSON.parse((window as any).AI_MONTHLY_CREDIT_ALLOCATIONS);

export const AI_CREDIT_PACK_DISPLAY_NAMES: { [lookupKey: string]: string } = {
  // Production keys
  plato_ai_credit_subscription_small: "Plato Basic",
  plato_ai_credit_subscription_medium: "Plato Standard",
  plato_ai_credit_subscription_large: "Plato Plus",
  plato_ai_credit_top_up_small: "Small",
  plato_ai_credit_top_up_medium: "Medium",
  plato_ai_credit_top_up_large: "Large",
  // Development keys
  ai_credit_pack_subscription_small_monthly: "Plato Basic",
  ai_credit_pack_subscription_large_monthly: "Plato Plus",
  ai_credit_pack_top_up_small: "Small",
  ai_credit_pack_top_up_large: "Large",
};

export const aiCreditPrices = (stripePrices: any[]) => {
  const prices: any[] = [];

  Object.keys(AI_CREDIT_PACK_CREDITS_BY_LOOKUP_KEY).forEach((lookupKey) => {
    const price = stripePrices.find((p: any) => p.lookupKey && p.lookupKey.includes(lookupKey));
    if (!price) return;

    prices.push({
      lookupKey,
      name: AI_CREDIT_PACK_DISPLAY_NAMES[lookupKey] || lookupKey,
      kind: price.type === "recurring" ? "subscription" : "one_off",
      credits: AI_CREDIT_PACK_CREDITS_BY_LOOKUP_KEY[lookupKey],
      priceId: price.id,
      priceDollars: price.unitAmount / 100,
      currency: price.currency,
      interval: price.recurring ? price.recurring.interval : null,
    });
  });

  return prices;
};
